import React, { useMemo } from 'react'
import { Box, Paper, Typography } from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import { axisClasses } from '@mui/x-charts/ChartsAxis'
import { formatNumber } from 'src/common/helpers'
import { SnapshotModel } from 'src/common/api/reportsSnapshot'
import { QCAD } from '../../utils'
import { buildRevenueChartData } from '../../revenueChartModel'

interface RevenueChartProps {
  snapshots: SnapshotModel[]
  fromDate: string
  toDate: string
}

const GROSS_SERIES_ID = 'grossEarned'
const RATE_SERIES_ID = 'rateApplied'
const GROSS_AXIS_ID = 'gross'
const RATE_AXIS_ID = 'rate'

const GROSS_COLOR = '#26ACC1'
const RATE_COLOR = '#F59E0B'

// Compact axis ticks (e.g. 1.2K) so large QCAD totals don't crowd the left axis.
const compact = (v: number): string =>
  Math.abs(v) >= 1000 ? `${(v / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}K` : formatNumber(v, 0, 0)

// Dual-axis history chart: cumulative gross QCAD earned (left, filled area) against the applied
// rate over the same window (right, dotted step line). The rate line is derived from the rate
// stamped on each day's snapshots (partners have no separate rate-schedule endpoint).
const RevenueChart: React.FC<RevenueChartProps> = ({ snapshots, fromDate, toDate }) => {
  const points = useMemo(() => buildRevenueChartData({ snapshots, fromDate, toDate }), [snapshots, fromDate, toDate])

  if (points.length === 0) return null

  const xLabels = points.map((p) => p.label)
  const cumulative = points.map((p) => p.cumulativeEarned)
  const ratePercent = points.map((p) => p.ratePercent)
  const hasRate = ratePercent.some((r) => r !== null)
  // ~10% headroom above the highest point of each line so neither touches the chart top.
  const grossMax = Math.max(0, ...cumulative)
  const rateMax = ratePercent.reduce<number>((max, r) => (r !== null && r > max ? r : max), 0)

  return (
    <Paper variant="card" sx={{ borderRadius: 1, p: 3, mb: 3 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 1 }}>Earnings & Rate History</Typography>
      <Box sx={{ width: '100%' }}>
        <LineChart
          height={340}
          margin={{ left: 80, right: 85, top: 48, bottom: 40 }}
          xAxis={[{ scaleType: 'point', data: xLabels }]}
          yAxis={[
            {
              id: GROSS_AXIS_ID,
              scaleType: 'linear',
              label: 'Gross QCAD Earned',
              valueFormatter: (v: number) => compact(v),
              min: 0,
              max: grossMax > 0 ? grossMax * 1.1 : undefined
            },
            {
              id: RATE_AXIS_ID,
              scaleType: 'linear',
              label: 'Net rate (annualized)',
              valueFormatter: (v: number) => `${Number(v).toFixed(2)}%`,
              min: 0,
              max: rateMax > 0 ? rateMax * 1.25 : undefined
            }
          ]}
          leftAxis={GROSS_AXIS_ID}
          rightAxis={hasRate ? RATE_AXIS_ID : null}
          series={[
            {
              id: GROSS_SERIES_ID,
              yAxisId: GROSS_AXIS_ID,
              data: cumulative,
              label: 'Gross QCAD Earned',
              area: true,
              showMark: false,
              color: GROSS_COLOR,
              valueFormatter: (v) => (v == null ? '' : `${formatNumber(v)} ${QCAD}`)
            },
            {
              id: RATE_SERIES_ID,
              yAxisId: RATE_AXIS_ID,
              data: ratePercent,
              label: 'Net rate (annualized)',
              curve: 'stepAfter',
              showMark: false,
              connectNulls: false,
              color: RATE_COLOR,
              valueFormatter: (v) => (v == null ? '' : `${Number(v).toFixed(2)}%`)
            }
          ]}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'middle' },
              padding: 0,
              itemMarkWidth: 10,
              itemMarkHeight: 10,
              markGap: 6,
              itemGap: 20,
              labelStyle: { fontSize: 12 }
            }
          }}
          sx={{
            // v7 pins the y-axis title ~28px from the axis line regardless of tick width, so push each
            // title outward past its tick labels (per MUI docs "Fixing overflow issues").
            [`& .${axisClasses.left} .${axisClasses.label}`]: { transform: 'translateX(-28px)' },
            [`& .${axisClasses.right} .${axisClasses.label}`]: { transform: 'translateX(34px)' },
            [`& .MuiLineElement-series-${RATE_SERIES_ID}`]: {
              strokeDasharray: '5 4',
              strokeWidth: 2
            },
            [`& .MuiAreaElement-series-${GROSS_SERIES_ID}`]: {
              fillOpacity: 0.12
            }
          }}
        />
      </Box>
    </Paper>
  )
}

export default RevenueChart
